import { Button } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import {
  formatGuidedEmptyMinutes,
  guidedEmptySuggestedLabel,
} from 'src/utils/growthEngineRecommendation'
import { SUGGESTED_DATA_REJECT_LABEL } from 'src/utils/growthEngineSuggestedData'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import OllieAskLink from 'src/components/ollie/OllieAskLink'
import useOllieAtLimit from 'src/hooks/useOllieAtLimit'

/**
 * In-place first-try empty state: why, how long, one CTA, suggested data,
 * and a human/demo row. Not a second page and not a modal.
 */
const GrowthEngineFeatureEmptyState = ({
  title,
  body,
  valueLine,
  estimatedMinutes,
  ctaLabel,
  onCta,
  suggestedItems = [],
  onSuggestedItem,
  onSuggestedEdit,
  onRejectSuggested,
  suggestedLabel = 'Start with what you already buy',
  honestNote,
  hideHumanPath = false,
  featureId,
  featureLabel,
  askOllieQuestion,
  askOllieLabel = 'Ask Ollie about this job',
  className,
  'data-testid': dataTestId = 'growth-engine-feature-empty-state',
}) => {
  // A drafted question can't be sent at the Ollie limit, so the link would drop it.
  const ollieAtLimit = useOllieAtLimit()
  if (!title && !body) return null

  const minutesLabel = formatGuidedEmptyMinutes(estimatedMinutes)
  const items = Array.isArray(suggestedItems)
    ? suggestedItems.filter(item => guidedEmptySuggestedLabel(item)).slice(0, 5)
    : []

  return (
    <Wrap
      className={className}
      data-testid={dataTestId}
      role='region'
      aria-label={title || 'Get started'}
    >
      {title ? <Title data-testid='guided-empty-title'>{title}</Title> : null}
      {body ? <Body data-testid='guided-empty-body'>{body}</Body> : null}
      {valueLine ? <ValueLine>{valueLine}</ValueLine> : null}
      {minutesLabel ? (
        <Minutes data-testid='guided-empty-minutes'>{minutesLabel}</Minutes>
      ) : null}
      {honestNote ? (
        <Minutes data-testid='guided-empty-honest-note'>{honestNote}</Minutes>
      ) : null}
      {onCta && ctaLabel ? (
        <Button type='primary' onClick={onCta} data-testid='guided-empty-cta'>
          {ctaLabel}
        </Button>
      ) : null}
      {askOllieQuestion && !ollieAtLimit ? (
        <AskOllieWrap data-testid='guided-empty-ask-ollie'>
          <OllieAskLink question={askOllieQuestion} label={askOllieLabel} />
        </AskOllieWrap>
      ) : null}
      {items.length ? (
        <Suggested>
          <SuggestedLabel>{suggestedLabel}</SuggestedLabel>
          <SuggestedList>
            {items.map((item, index) => (
              <SuggestedRow
                key={
                  item.productId ||
                  item.purchaseOrderNumber ||
                  item.venueSupplierId ||
                  `${guidedEmptySuggestedLabel(item)}-${index}`
                }
              >
                <SuggestedItem
                  type='button'
                  data-testid='guided-empty-suggested-item'
                  onClick={() =>
                    onSuggestedItem ? onSuggestedItem(item) : onCta && onCta()
                  }
                >
                  {guidedEmptySuggestedLabel(item)}
                </SuggestedItem>
                {onSuggestedEdit ? (
                  <SuggestedEdit
                    type='button'
                    data-testid='guided-empty-suggested-edit'
                    onClick={() => onSuggestedEdit(item)}
                  >
                    Edit
                  </SuggestedEdit>
                ) : null}
              </SuggestedRow>
            ))}
          </SuggestedList>
          {onRejectSuggested ? (
            <RejectSuggested
              type='button'
              data-testid='guided-empty-suggested-reject'
              onClick={onRejectSuggested}
            >
              {SUGGESTED_DATA_REJECT_LABEL}
            </RejectSuggested>
          ) : null}
        </Suggested>
      ) : null}
      {hideHumanPath ? null : (
        <GrowthEngineHumanDemoRow
          featureId={featureId}
          featureLabel={featureLabel}
        />
      )}
    </Wrap>
  )
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 8px 24px;
`

const AskOllieWrap = styled.div`
  button {
    margin: 0;
  }
`

const Title = styled.h3`
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Body = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#595959'};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`

const ValueLine = styled.p`
  color: ${({ theme }) => theme.colours.seaweedGreen || '#389e0d'};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const Minutes = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  font-size: 13px;
  margin: 0 0 6px;
`

const Suggested = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`

const SuggestedLabel = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  font-size: 12px;
  font-weight: 600;
  margin: 0;
`

const SuggestedList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const SuggestedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const SuggestedItem = styled.button`
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border: 1px solid ${({ theme }) => theme.colours.borderGrey || '#e8e8e8'};
  border-radius: 999px;
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  cursor: pointer;
  font-size: 13px;
  padding: 6px 12px;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  }
`

const SuggestedEdit = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  cursor: pointer;
  font-size: 12px;
  padding: 4px 6px;
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  }
`

const RejectSuggested = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colours.oliveGrey || '#8c8c8c'};
  cursor: pointer;
  font-size: 13px;
  padding: 4px 0;
  text-align: left;
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
  }
`

export default GrowthEngineFeatureEmptyState
